import { useEffect, useState } from "react";

/**
 * Equipment map markers. Ported to TypeScript from the design drop's EquipmentMarker.jsx, keeping
 * only what the machine-positions map actually uses.
 *
 * Every silhouette is drawn nose-up in a 64x64 box centred on the machine's position, so orienting
 * a marker is just a rotation by the reported bearing: heading 0 is north and angles increase
 * clockwise, which is exactly what a GPS course-over-ground value gives us.
 */

/** Note: these match both the sprite's symbol ids and the strings the API's EquipmentType returns. */
export type EquipmentType =
  | "haul-truck"
  | "truck-10-wheel"
  | "loader"
  | "excavator"
  | "drill-rig"
  | "dozer"
  | "grader"
  | "breaker"
  | "fire-truck"
  | "water-truck";

const EQUIPMENT_LABELS: Record<EquipmentType, string> = {
  "haul-truck": "Production truck",
  "truck-10-wheel": "10-wheel truck",
  loader: "Loader",
  excavator: "Excavator",
  "drill-rig": "Drill rig",
  dozer: "Dozer",
  grader: "Grader",
  breaker: "Breaker",
  "fire-truck": "Fire truck",
  "water-truck": "Water truck",
};

/** Note: the sprite is expected to define a symbol for every one of these, and is rejected outright
 *  if it does not - see useEquipmentSprite. */
const EQUIPMENT_IDS = Object.keys(EQUIPMENT_LABELS) as EquipmentType[];

export function isEquipmentType(value: string | null | undefined): value is EquipmentType {
  return value != null && value in EQUIPMENT_LABELS;
}

export function equipmentLabel(type: EquipmentType): string {
  return EQUIPMENT_LABELS[type];
}

/** Note: from the design drop. Deliberately extends past the nose of the box, so any element
 *  wrapping it must keep overflow visible or the wedge is clipped at the tip. */
const WEDGE_PATH = "M32 32 L14.8 -10.6 A46 46 0 0 1 49.2 -10.6 Z";

/**
 * Status-dot colours for the states that have no stoppage type of their own. A machine that is DOWN
 * takes its dot straight from tblStoppageType.ColourHex, so the map speaks the same colour language as
 * the Stoppages screen instead of inventing a parallel one.
 *
 * Note: status is a separate dot rather than a recolour of the body, and the body is now left at
 * whatever colour the sprite designed for it. Repainting bodies collided with the classes that are
 * identified BY colour: Maintenance Down is #ff0909 and the fire appliance is fire red, so a haul truck
 * on a breakdown became indistinguishable from an emergency vehicle. Shape carries the machine, the dot
 * carries the state, and neither has to borrow the other's channel.
 *
 * Note: `working` is the same green the Stoppages screen uses for UP.
 *
 * Note: `unclassified` covers a machine that is down against a stoppage type carrying no colour, which
 * in practice means "Unclassified (Auto-Detected)" - the type every GPS-derived stoppage is raised
 * against. Grey is the honest answer there: it is stopped, and nobody has said why yet.
 */
export const STATUS_COLOURS = {
  working: "#44cc44",
  unclassified: "#7E8B96",
  offline: "#7E8B96",
} as const;

/** Note: how far an offline machine's marker is faded. It stays at its last known position because
 *  that is real information, but it must not compete with machines that are actually working. */
export const OFFLINE_OPACITY = 0.4;

/**
 * Colour of the travel-direction wedge, applied to the sprite's --veh-heading.
 *
 * Note: it has to be set here rather than left to the sprite's own default, which is white fading out
 * from 50% opacity - correct for a dark basemap, and effectively absent on the OSM light tiles this map
 * uses, where white over the #F2EFE9 land fill is about 1.05:1. This is the keyline colour the
 * silhouettes are already drawn with, so it reads as a direction cone at roughly 3.4:1 without
 * introducing a colour that isn't in the asset's palette.
 *
 * Note: and it has to be set on the sprite HOST, not on each marker, which is the non-obvious part.
 * `--veh-fill` works from the marker because .b lives inside the <symbol> and is cloned into the
 * <use> shadow tree, which inherits from the marker. The gradient does not: `fill="url(#eq-wedge-fade)"`
 * resolves its stops where the <linearGradient> element itself sits - in the injected sprite - so a
 * custom property set on the marker never reaches it, and the stops would keep resolving to the
 * white default.
 */
const HEADING_WEDGE_COLOUR = "#1B1F24";

let spriteLoad: Promise<void> | null = null;
let spriteFailed = false;

export type SpriteStatus = "loading" | "ready" | "failed";

/**
 * Injects the marker sprite into the document once, so `<use href="#eq-...">` can resolve anywhere
 * on the page. Mount above the map, and use the returned status to fall back to plain markers if it
 * reports "failed" - a `<use>` pointing at an absent symbol renders nothing at all, silently.
 *
 * Note: this has to be a real fetch-and-inject rather than an <img> or external `use href`, because
 * an SVG referenced as an external document cannot be recoloured by the CSS custom properties the
 * sprite is built around, and the whole status-colour scheme depends on them.
 */
export function useEquipmentSprite(src = "/icons/eclipse-equipment-markers.svg"): SpriteStatus {
  const [status, setStatus] = useState<SpriteStatus>(
    () => document.getElementById("eclipse-equipment-sprite") ? "ready" : spriteFailed ? "failed" : "loading");

  useEffect(() => {
    let mounted = true;

    if (document.getElementById("eclipse-equipment-sprite")) {
      setStatus("ready");
      return;
    }

    if (!spriteLoad) {
      // Note: cleared per attempt, so a remount after a transient network failure gets a real retry
      // rather than inheriting the previous verdict.
      spriteFailed = false;
      spriteLoad = fetch(src)
        .then((response) => {
          if (!response.ok) throw new Error(`Sprite request returned ${response.status}`);
          return response.text();
        })
        .then((markup) => {
          if (document.getElementById("eclipse-equipment-sprite")) return;

          const host = document.createElement("div");
          host.setAttribute("aria-hidden", "true");
          host.style.display = "none";
          host.innerHTML = markup;

          // Note: a 200 is NOT evidence the sprite arrived. A single-page app serves index.html for
          // any unmatched path, so a wrong or renamed sprite URL comes back 200 with an HTML body -
          // verified against this dev server, which answered a deliberately bad icon path with 200
          // text/html. Injecting that resolves the promise and claims the id that suppresses any
          // retry, leaving every marker an empty box: a hard asset failure presented as a healthy
          // map, silently, because `use href` on a missing symbol renders nothing and reports
          // nothing. So parse first and check the symbols are really there, and only claim the id
          // once they are.
          const symbols = EQUIPMENT_IDS.filter(id => host.querySelector(`#eq-${id}`) == null);
          if (symbols.length > 0) {
            throw new Error(
              `Response from ${src} is not a complete marker sprite - missing symbol(s) `
              + `${symbols.map(id => `eq-${id}`).join(", ")}. A single-page-app fallback serving `
              + "index.html is the usual cause of all of them being absent.");
          }

          host.id = "eclipse-equipment-sprite";
          host.style.setProperty("--veh-heading", HEADING_WEDGE_COLOUR);
          document.body.prepend(host);
        })
        .catch((error) => {
          // Note: said out loud rather than swallowed, and the flag is what lets the map fall back to
          // plain markers. Without the sprite every silhouette renders as an empty box, which reads as
          // "no machines are reporting" rather than "an asset is missing".
          spriteLoad = null;
          spriteFailed = true;
          console.error(`Equipment marker sprite failed to load from ${src}.`, error);
        });
    }

    spriteLoad?.then(() => {
      if (mounted) setStatus(spriteFailed ? "failed" : "ready");
    });

    return () => {
      mounted = false;
    };
  }, [src]);

  return status;
}

export interface EquipmentMarkerOptions {
  type: EquipmentType;
  /** Degrees clockwise from north. */
  heading: number;
  size: number;
  /** Shows the travel-direction wedge. */
  moving: boolean;
  /** Status-dot fill, already resolved by the caller - a stoppage type's ColourHex, or a STATUS_COLOURS entry. */
  statusColour: string;
  /** 1 for a live machine; below 1 for a stale one. Defaults to fully opaque. */
  opacity?: number;
}

/**
 * Marker markup for Leaflet's divIcon, which takes an HTML string rather than a React node.
 *
 * Note: the inline style is the sprite's documented interface, not ad-hoc styling - `--veh-fill`
 * is how the asset is recoloured, and `overflow:visible` is required by the heading wedge. Neither
 * has a Tailwind equivalent, and a stylesheet rule could not carry a per-marker rotation.
 */
export function equipmentMarkerHtml(
  { type, heading, size, moving, statusColour, opacity = 1 }: EquipmentMarkerOptions): string {
  const wedge = moving ? `<path d="${WEDGE_PATH}" fill="url(#eq-wedge-fade)"/>` : "";

  // Note: --veh-fill is not set at all. Each symbol keeps the colour it was designed with, so a machine
  // always looks like itself and the classes identified BY colour stay unambiguous.
  // Note: two layers, because only the silhouette may rotate. A dot drawn inside the rotating <svg>
  // would orbit the machine as it turned - fixed to the marker in screen space is the whole point of a
  // status badge. The dot is emitted after the svg so it paints over the wedge where they overlap.
  const dotSize = Math.max(8, Math.round(size * 0.28));

  return (
    `<div style="position:relative;width:${size}px;height:${size}px` +
    `${opacity < 1 ? `;opacity:${opacity}` : ""}">` +
    `<svg viewBox="0 0 64 64" width="${size}" height="${size}" aria-hidden="true" ` +
    `style="overflow:visible;transform:rotate(${heading}deg);transform-origin:50% 50%">` +
    `${wedge}<use href="#eq-${type}"/></svg>` +
    `<span style="position:absolute;top:0;right:0;width:${dotSize}px;height:${dotSize}px;` +
    `border-radius:50%;background:${statusColour};border:2px solid #fff;` +
    `box-shadow:0 0 0 1px rgba(0,0,0,.45)"></span>` +
    `</div>`
  );
}

/**
 * Bearing in degrees between two positions, for a feed that reports position but no course.
 *
 * Note: returns null below `minMetres` so a parked machine does not spin on GPS jitter. Hold the
 * last known good bearing in that case rather than snapping to an arbitrary one.
 */
export function bearingBetween(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
  minMetres = 5,
): number | null {
  const earthRadius = 6371000;
  const rad = Math.PI / 180;
  const dLat = (to.lat - from.lat) * rad;
  const dLng = (to.lng - from.lng) * rad;
  const midLat = ((from.lat + to.lat) / 2) * rad;

  if (earthRadius * Math.hypot(dLat, dLng * Math.cos(midLat)) < minMetres) return null;

  const y = Math.sin(dLng) * Math.cos(to.lat * rad);
  const x = Math.cos(from.lat * rad) * Math.sin(to.lat * rad)
    - Math.sin(from.lat * rad) * Math.cos(to.lat * rad) * Math.cos(dLng);

  return ((Math.atan2(y, x) / rad) + 360) % 360;
}

/**
 * Shortest-path rotation. Note: without this a machine crossing north animates the long way round,
 * unwinding 357 degrees to get from 359 to 2.
 */
export function unwrapHeading(previous: number | null, next: number): number {
  if (previous == null) return next;
  return previous + ((((next - previous) % 360) + 540) % 360) - 180;
}
